import React, {
  Dispatch, FormEvent, SetStateAction, useMemo, useState,
} from 'react';
import {
  Button,
  Checkbox,
  Dialog, DialogActions,
  DialogContent, FormControlLabel,
  IconButton,
  MenuItem, Radio, RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MdHelpOutline, MdOutlineClose } from 'react-icons/md';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import styled from '@emotion/styled/macro';
import Divider from '@mui/material/Divider';
import { toast } from 'react-toastify';
import { isAxiosError } from 'axios';
import { RoleType } from 'bluetail-globals';
import useEditAccountUser from 'accountUsers/hooks/useEditAccountUser';
import { useModalStore } from '../../../common/hooks/useModals';
import { serializeFormData } from '../../../common/util/serializeFormData';

const ModalHeader = styled.div`
  font-size: 20px;
  padding: 12px;
`;

const ModalBody = styled.div`
  display: flex;
  gap: 9px;
  flex-direction: column;
`;

const ModalDivider = styled(Divider)`
`;

const ModalHeaderActionContainer = styled(Stack)`
  justify-content: space-between;
`;

const CloseButton = styled(Button)`
  font-size: 20px;
  color: black;
`;

const SubmitButton = styled(Button)`
  width: 120px;
`;

const ErrorTypography = styled(Typography)`
  color: red;
`;

const LoginExpiresTypography = styled(Typography)`
  white-space: pre;
`;

const userTypeSelectOptions = ['Broker', 'Lender', 'Maintenance', 'MRO', 'OEM', 'Other', 'Pilot', 'Regulator'];

type DataModel = {
    firstName: string;
    lastName: string;
    email: string;
    userType: string;
    loginNeverExpires: boolean;
    loginExpirationDate: Date | null;
    role: number;
}

type EditUserInfoUser = {
    company_id: number;
    user_id: number;
    email: string;
    first_name: string;
    last_name: string;
    marketing_user_type: string | null;
    role_id: number;
    expire_date: string;
}

type LoginExpirationInfo =
    Pick<DataModel, 'loginExpirationDate' | 'loginNeverExpires'>
    & { validationMessage: string; };

type ViewProps = {
    handleClose: () => void;
    handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
    model: DataModel;
    loginExpiration: LoginExpirationInfo;
    setLoginExpiration: Dispatch<SetStateAction<LoginExpirationInfo>>;
    roleValidationMessage: string;
    setRoleValidationMessage: Dispatch<SetStateAction<string>>;
    emailValidationMessage: string;
    setEmailValidationMessage: Dispatch<SetStateAction<string>>;
    userTypeValidationMessage: string;
    setUserTypeValidationMessage: Dispatch<SetStateAction<string>>;
};

function View({
  handleClose,
  handleSubmit,
  model,
  loginExpiration,
  setLoginExpiration,
  roleValidationMessage,
  setRoleValidationMessage,
  emailValidationMessage,
  setEmailValidationMessage,
  userTypeValidationMessage,
  setUserTypeValidationMessage,
}:ViewProps): React.JSX.Element {
  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <ModalHeaderActionContainer spacing={2} direction="row">
          <ModalHeader>
            Edit User Info -
            {' '}
            {`${model.firstName} ${model.lastName}`}
          </ModalHeader>
          <CloseButton variant="text" onClick={handleClose}>
            <MdOutlineClose />
          </CloseButton>
        </ModalHeaderActionContainer>
        <ModalDivider />
        <DialogContent>
          <ModalBody>
            <Grid container alignItems="center">
              <Grid item xs={2}>
                <InputLabel>First Name</InputLabel>
              </Grid>
              <Grid item xs={7}>
                <TextField
                  name="firstName"
                  size="small"
                  required
                  fullWidth
                  defaultValue={model.firstName}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center">
              <Grid item xs={2}>
                <InputLabel>Last Name</InputLabel>
              </Grid>
              <Grid item xs={7}>
                <TextField
                  name="lastName"
                  size="small"
                  required
                  fullWidth
                  defaultValue={model.lastName}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center">
              <Grid item xs={2}>
                <InputLabel>Email Address</InputLabel>
              </Grid>
              <Grid item xs={7}>
                <TextField
                  name="email"
                  size="small"
                  required
                  fullWidth
                  defaultValue={model.email}
                  onChange={() => setEmailValidationMessage('')}
                />
              </Grid>
              <Grid item xs={3}>
                {!!emailValidationMessage && (
                <ErrorTypography>
                  *
                  {' '}
                  {emailValidationMessage}
                </ErrorTypography>
                )}
              </Grid>
            </Grid>

            <Grid container alignItems="center">
              <Grid item xs={2}>
                <InputLabel>User Type</InputLabel>
              </Grid>
              <Grid item xs={7}>
                <Stack direction="row" alignItems="center">
                  <TextField
                    select
                    name="userType"
                    size="small"
                    required
                    fullWidth
                    defaultValue={model.userType}
                    onChange={() => setUserTypeValidationMessage('')}
                  >
                    {userTypeSelectOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        <Typography>{opt}</Typography>
                      </MenuItem>
                    ))}
                  </TextField>
                  <Tooltip title={<Typography>This info helps us better understand who is using our system</Typography>}>
                    <IconButton>
                      <MdHelpOutline fontSize="28px" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Grid>
              <Grid item xs={3}>
                {!!userTypeValidationMessage && (
                <ErrorTypography>
                  *
                  {' '}
                  {userTypeValidationMessage}
                </ErrorTypography>
                )}
              </Grid>
            </Grid>

            <Grid container alignItems="center">
              <Grid item xs={2}>
                <InputLabel>Login Expires</InputLabel>
              </Grid>
              <Grid item xs={7}>
                <Stack direction="row" alignItems="center">
                  <Checkbox
                    name="loginNeverExpires"
                    checked={loginExpiration.loginNeverExpires}
                    onChange={(e) => setLoginExpiration({
                      loginExpirationDate: null,
                      loginNeverExpires: e.target.checked,
                      validationMessage: '',
                    })}
                  />
                  <LoginExpiresTypography>
                    Never, or
                    {'  '}
                  </LoginExpiresTypography>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      format="MM-DD-YYYY"
                      disabled={loginExpiration.loginNeverExpires}
                      // Floor at today, except when the user already has an expiry in the
                      // past — that value has to stay selectable so it can be displayed.
                      minDate={moment.min(moment(), moment(model.loginExpirationDate ?? undefined))}
                      value={loginExpiration.loginExpirationDate ? moment(loginExpiration.loginExpirationDate) : null}
                      onChange={(x: Moment | null) => setLoginExpiration({
                        loginNeverExpires: false,
                        loginExpirationDate: x?.isValid() ? x.toDate() : null,
                        validationMessage: '',
                      })}
                      slotProps={{ textField: { size: 'small' } }}
                    />
                  </LocalizationProvider>
                </Stack>
              </Grid>
              <Grid item xs={3}>
                {!!loginExpiration.validationMessage && (
                <ErrorTypography>
                  *
                  {' '}
                  {loginExpiration.validationMessage}
                </ErrorTypography>
                )}
              </Grid>
            </Grid>

            <Grid container alignItems="start">
              <Grid item xs={2}>
                <InputLabel>Role</InputLabel>
              </Grid>
              <Grid item xs={7}>
                <RadioGroup
                  defaultValue={model.role}
                  name="role"
                  onChange={() => setRoleValidationMessage('')}
                >
                  <FormControlLabel
                    value={4}
                    control={<Radio />}
                    label="Viewer:  Access to view, search, and print all documents, but cannot make any edits"
                  />
                  <FormControlLabel
                    value={6}
                    control={<Radio />}
                    label="Buyer:  Access to view logbooks only, but cannot access Categories"
                  />
                  <FormControlLabel
                    value={7}
                    control={<Radio />}
                    label="MRO:  Access to upload, search, view, print, and download documents"
                  />
                  <FormControlLabel
                    value={3}
                    control={<Radio />}
                    label="Contributor:  Access to upload, view, and search all documents, but cannot make admin changes"
                  />
                  <FormControlLabel
                    value={2}
                    control={<Radio />}
                    label="Admin:  Full control over all admin functions, including managing users, access, aircraft, and logbooks"
                  />
                  <FormControlLabel
                    value={1}
                    control={<Radio />}
                    label="Owner"
                  />
                </RadioGroup>
              </Grid>
              <Grid item xs={2}>
                {!!roleValidationMessage && (
                <ErrorTypography>
                  *
                  {' '}
                  {roleValidationMessage}
                </ErrorTypography>
                )}
              </Grid>
            </Grid>
          </ModalBody>
        </DialogContent>
        <ModalDivider />
        <DialogActions>
          <Stack spacing={2} direction="row" justifyContent="end" width="100%">
            <SubmitButton variant="contained" color="primary" type="submit">
              Save
            </SubmitButton>
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
}

type EditAccountUserModalStore = {
    editAccountUser: { user: EditUserInfoUser; refreshUserList: () => void; };
    setEditAccountUserState: (state: boolean) => void;
};

function Model(): React.JSX.Element {
  const { editAccountUser: { user, refreshUserList }, setEditAccountUserState } = useModalStore() as EditAccountUserModalStore;
  const mutation = useEditAccountUser(user.user_id);
  const handleClose = (): void => setEditAccountUserState(false);
  const [roleValidationMessage, setRoleValidationMessage] = useState('');
  const [emailValidationMessage, setEmailValidationMessage] = useState('');
  const [userTypeValidationMessage, setUserTypeValidationMessage] = useState('');

  const model = useMemo<DataModel>(() => {
    const parsedExpireDate = new Date(user.expire_date);
    const loginNeverExpires = Number.isNaN(parsedExpireDate.getTime());

    return {
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      role: user.role_id,
      userType: user.marketing_user_type,
      loginNeverExpires,
      loginExpirationDate: loginNeverExpires ? null : parsedExpireDate,
    };
  }, [user]);

  const [loginExpiration, setLoginExpiration] = useState<LoginExpirationInfo>({
    loginNeverExpires: model.loginNeverExpires,
    loginExpirationDate: model.loginExpirationDate,
    validationMessage: '',
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    const formValues = serializeFormData(e) as DataModel;
    formValues.loginExpirationDate = loginExpiration.loginExpirationDate;
    formValues.loginNeverExpires = loginExpiration.loginNeverExpires;

    let isFormValid = true;

    if (!formValues.email) {
      setEmailValidationMessage('Required');
      isFormValid = false;
    } else if (!formValues.email.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)) {
      setEmailValidationMessage('Invalid Email');
      isFormValid = false;
    }

    if (!formValues.role) {
      setRoleValidationMessage('Role is required');
      isFormValid = false;
    }

    if (!userTypeSelectOptions.includes(formValues.userType)) {
      setUserTypeValidationMessage('User Type is required');
      isFormValid = false;
    }

    if (!formValues.loginNeverExpires && !formValues.loginExpirationDate) {
      setLoginExpiration((p) => ({
        ...p,
        validationMessage: 'Required',
      }));
      isFormValid = false;
    }

    if (!isFormValid) {
      return;
    }

    if (formValues.loginExpirationDate) {
      formValues.loginExpirationDate.setHours(23, 59, 59);
    }

    formValues.role = Number(formValues.role);

    mutation.mutate({
      company_id: user.company_id,
      first_name: formValues.firstName,
      last_name: formValues.lastName,
      email: formValues.email,
      marketing_user_type: formValues.userType,
      login_expiry: formValues.loginNeverExpires ? null : formValues.loginExpirationDate.toISOString(),
      role_id: formValues.role as RoleType,
    }, {
      onSuccess: () => {
        toast.success(`User ${formValues.firstName} ${formValues.lastName} updated`);
        refreshUserList();
        handleClose();
      },
      onError: (error) => {
        // Surface the server's validation errors (e.g. "Missing or invalid body parameter ...") rather than a bare message.
        const serverErrors = isAxiosError<{ message?: string; errors?: string[] }>(error) ? error.response?.data : undefined;
        const detail = serverErrors?.errors?.length ? serverErrors.errors.join(' ') : serverErrors?.message;
        toast.error(detail ? `Error updating user: ${detail}` : 'Error updating user');
      },
    });
  };

  const viewProps: ViewProps = {
    model,
    handleSubmit,
    handleClose,
    loginExpiration,
    setLoginExpiration,
    roleValidationMessage,
    setRoleValidationMessage,
    emailValidationMessage,
    setEmailValidationMessage,
    userTypeValidationMessage,
    setUserTypeValidationMessage,
  };

  return <View {...viewProps} />;
}

export default Model;
export { View };
